import "@/utils/simplePeerPolyfill";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { io, type Socket } from "socket.io-client";
import SimplePeer, { type Instance as SimplePeerInstance, type SignalData } from "simple-peer";
import {
  FRIENDLY_ERROR_MESSAGE,
  LAN_ONLY_ICE_SERVERS,
  NETWORK_DROP_MESSAGE,
  REMOTE_ERROR_EVENT,
  REMOTE_TERMINATED_EVENT,
  TRANSPORTS,
  derivePeerRoom,
  resolveCounterpartRole,
  resolveSocketUrl,
  type ClearPeerOptions,
  type ClearSocketOptions,
  type PeerDeviceRole,
  type PeerStatus,
  type StartPeerConnectionOptions,
} from "@/types/peerConnection";
import type {
  PeerInterviewCompleteSignal,
  PeerMessage,
  RemoteDetectionStatus,
} from "@/types/peerDetection";

export type { PeerDeviceRole, PeerStatus } from "@/types/peerConnection";

export type LanStatus = "unknown" | "matched" | "mismatched";

export type PeerNetworkDescriptor = {
  rawAddress: string | null;
  normalizedAddress: string | null;
  normalizedSignature: string | null;
  addressFamily: "ipv4" | "ipv6" | null;
  updatedAt?: number | null;
};

export type LanStatusDetail = {
  laptop: PeerNetworkDescriptor | null;
  mobile: PeerNetworkDescriptor | null;
} | null;

type AutoReconnectOptions = {
  destroyLocalStream?: boolean;
  delayMs?: number;
};

const AUTO_RECONNECT_DELAY_MS = 2000;
const AUTO_RECONNECT_MAX_ATTEMPTS = 3;

export const usePeerConnectionManager = () => {
  const socketRef = useRef<Socket | null>(null);
  const peerRef = useRef<SimplePeerInstance | null>(null);
  const activeConnectionIdRef = useRef<string | null>(null);
  const lastRequestedConnectionIdRef = useRef<string | null>(null);
  const lastRequestedDeviceRoleRef = useRef<PeerDeviceRole | null>(null);
  const activePeerRoomRef = useRef<string | null>(null);
  const counterpartPeerRoomRef = useRef<string | null>(null);
  const activePeerDeviceRoleRef = useRef<PeerDeviceRole | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const remoteStreamRef = useRef<MediaStream | null>(null);
  const removeRemoteTrackListenersRef = useRef<(() => void) | null>(null);
  const socketConnectionPromiseRef = useRef<Promise<void> | null>(null);
  const peerStatusRef = useRef<PeerStatus>("idle");
  const autoReconnectEnabledRef = useRef(true);
  const autoReconnectAttemptsRef = useRef(0);
  const autoReconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastStartPeerOptionsRef = useRef<Pick<StartPeerConnectionOptions, "initiator"> | null>(null);
  const scheduleAutoReconnectRef = useRef<((options?: AutoReconnectOptions) => void) | null>(null);

  const [isSocketConnected, setIsSocketConnected] = useState(false);
  const [socketId, setSocketId] = useState<string | null>(null);
  const [peerStatus, setPeerStatus] = useState<PeerStatus>("idle");
  const [peerError, setPeerError] = useState<string | null>(null);
  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null);
  const [remoteStreamVersion, setRemoteStreamVersion] = useState(0);
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [mobileHasJoined, setMobileHasJoined] = useState(false);
  const [remoteDetectionStatus, setRemoteDetectionStatus] = useState<RemoteDetectionStatus | null>(null);
  const [lanStatus, setLanStatus] = useState<LanStatus>("unknown");
  const [lanStatusDetail, setLanStatusDetail] = useState<LanStatusDetail>(null);
  const [lanMatchedPeerFailed, setLanMatchedPeerFailed] = useState(false);
  const [isMobileBypassed, setIsMobileBypassed] = useState(false);
  const [hasAttemptedPeerConnection, setHasAttemptedPeerConnection] = useState(false);
  const [interviewCompleteSignal, setInterviewCompleteSignal] = useState<PeerInterviewCompleteSignal | null>(null);

  const markPeerNegotiationAttempt = useCallback(() => {
    setHasAttemptedPeerConnection((prev) => (prev ? prev : true));
  }, []);

  const isPeerConnected = useMemo(() => peerStatus === "connected", [peerStatus]);
  const markMobileBypassed = useCallback((nextValue: boolean) => {
    setIsMobileBypassed(nextValue);
  }, []);

  const cancelScheduledAutoReconnect = useCallback(() => {
    if (autoReconnectTimeoutRef.current) {
      clearTimeout(autoReconnectTimeoutRef.current);
      autoReconnectTimeoutRef.current = null;
    }
  }, []);

  const disableAutoReconnect = useCallback(() => {
    autoReconnectEnabledRef.current = false;
    autoReconnectAttemptsRef.current = 0;
    cancelScheduledAutoReconnect();
  }, [cancelScheduledAutoReconnect]);

  const enableAutoReconnect = useCallback(() => {
    autoReconnectEnabledRef.current = true;
    autoReconnectAttemptsRef.current = 0;
  }, []);

  const stopLocalTracks = useCallback(() => {
    if (!localStreamRef.current) {
      return;
    }
    localStreamRef.current.getTracks().forEach((track) => {
      try {
        track.stop();
      } catch (error) {
        console.warn("[PeerConnectionManager] Failed to stop track cleanly", error);
      }
    });
    localStreamRef.current = null;
    setLocalStream(null);
  }, []);

  const clearPeerInstance = useCallback(
    ({ destroyLocalStream, nextStatus, nextError }: ClearPeerOptions = {}) => {
      if (peerRef.current) {
        try {
          peerRef.current.removeAllListeners();
          peerRef.current.destroy();
        } catch (error) {
          console.error("[PeerConnectionManager] Failed to destroy peer", error);
        }
      }
      peerRef.current = null;

      if (removeRemoteTrackListenersRef.current) {
        removeRemoteTrackListenersRef.current();
        removeRemoteTrackListenersRef.current = null;
      }

      if (destroyLocalStream) {
        stopLocalTracks();
      }

      remoteStreamRef.current = null;
      setRemoteStream(null);
      setRemoteStreamVersion((prev) => prev + 1);
      setRemoteDetectionStatus(null);
      setInterviewCompleteSignal(null);
      setLanMatchedPeerFailed(false);

      const resolvedStatus = nextStatus ?? "idle";
      setPeerStatus(resolvedStatus);
      peerStatusRef.current = resolvedStatus;
      setPeerError(nextError ?? null);
    },
    [stopLocalTracks],
  );

  const clearSocketInstance = useCallback(
    ({ disconnect = true }: ClearSocketOptions = {}) => {
      if (socketRef.current) {
        try {
          socketRef.current.removeAllListeners();
          if (disconnect) {
            socketRef.current.disconnect();
          }
        } catch (error) {
          console.error("[PeerConnectionManager] Failed to disconnect socket", error);
        }
      }

      socketRef.current = null;
      if (disconnect) {
        activeConnectionIdRef.current = null;
      }
      activePeerRoomRef.current = null;
      counterpartPeerRoomRef.current = null;
      activePeerDeviceRoleRef.current = null;
      socketConnectionPromiseRef.current = null;
      setRemoteDetectionStatus(null);
      setInterviewCompleteSignal(null);
      setIsSocketConnected(false);
      setSocketId(null);
      setMobileHasJoined(false);
      setLanStatus("unknown");
      setLanStatusDetail(null);
      setLanMatchedPeerFailed(false);
      setHasAttemptedPeerConnection(false);
      cancelScheduledAutoReconnect();
      autoReconnectAttemptsRef.current = 0;
    },
    [cancelScheduledAutoReconnect],
  );

  const resetConnectionState = useCallback(
    (destroyLocalStream = false) => {
      clearPeerInstance({
        destroyLocalStream,
        nextStatus: destroyLocalStream ? "closed" : "idle",
        nextError: destroyLocalStream ? FRIENDLY_ERROR_MESSAGE : null,
      });
      clearSocketInstance();
      lastRequestedConnectionIdRef.current = null;
      lastRequestedDeviceRoleRef.current = null;
      setIsMobileBypassed(false);
      lastStartPeerOptionsRef.current = null;
      disableAutoReconnect();
    },
    [clearPeerInstance, clearSocketInstance, disableAutoReconnect],
  );

  const attachPeerListeners = useCallback(
    (peer: SimplePeerInstance) => {
      const handleRemoteStreamTerminated = () => {
        clearPeerInstance({
          destroyLocalStream: false,
          nextStatus: "closed",
          nextError: NETWORK_DROP_MESSAGE,
        });
        scheduleAutoReconnectRef.current?.();
      };

      peer.on("signal", (signal: SignalData) => {
        if (!activeConnectionIdRef.current || !socketRef.current) {
          return;
        }
        markPeerNegotiationAttempt();
        setPeerStatus("signaling");
        peerStatusRef.current = "signaling";
        const targetPeerRoom =
          counterpartPeerRoomRef.current ?? activeConnectionIdRef.current ?? undefined;
        const originPeerRoom = activePeerRoomRef.current ?? undefined;
        const originPeerDeviceRole = activePeerDeviceRoleRef.current ?? undefined;
        socketRef.current.emit("peer-connection:signal", {
          peerConnectionId: activeConnectionIdRef.current,
          targetPeerRoom,
          originPeerRoom,
          originPeerDeviceRole,
          signal,
        });
      });

      peer.on("stream", (stream: MediaStream) => {
        setPeerError(null);
        remoteStreamRef.current = stream;
        setRemoteStream(stream);
        setRemoteStreamVersion((prev) => prev + 1);
        setPeerStatus("connected");
        peerStatusRef.current = "connected";

        removeRemoteTrackListenersRef.current?.();
        const cleanupCallbacks: Array<() => void> = [];

        stream.getTracks().forEach((track) => {
          const handleEnded = () => handleRemoteStreamTerminated();
          track.addEventListener("ended", handleEnded);
          cleanupCallbacks.push(() => track.removeEventListener("ended", handleEnded));
        });

        const handleInactive = () => handleRemoteStreamTerminated();
        if ("addEventListener" in stream) {
          stream.addEventListener("inactive", handleInactive);
          cleanupCallbacks.push(() => {
            stream.removeEventListener("inactive", handleInactive);
          });
        }

        removeRemoteTrackListenersRef.current = () => {
          cleanupCallbacks.forEach((cb) => {
            try {
              cb();
            } catch {
              // noop
            }
          });
        };
      });

      peer.on("connect", () => {
        setPeerError(null);
        setPeerStatus("connected");
        peerStatusRef.current = "connected";
      });

      peer.on("close", () => {
        clearPeerInstance({
          destroyLocalStream: false,
          nextStatus: "closed",
          nextError: FRIENDLY_ERROR_MESSAGE,
        });
        scheduleAutoReconnectRef.current?.();
      });

      peer.on("error", (error: Error) => {
        console.error("[PeerConnectionManager] Peer error", error);
        const isHandshakeStateError =
          error.message.includes("setRemoteDescription") || error.message.includes("Called in wrong state");
        const isNetworkError =
          error.message.includes("setRemoteDescription") ||
          error.message.includes("InvalidStateError") ||
          error.message.includes("Called in wrong state");

        if (isHandshakeStateError) {
          if (socketRef.current && activeConnectionIdRef.current) {
            const targetPeerRoom =
              counterpartPeerRoomRef.current ?? activeConnectionIdRef.current ?? undefined;
            const originPeerRoom = activePeerRoomRef.current ?? undefined;
            const originPeerDeviceRole = activePeerDeviceRoleRef.current ?? undefined;
            socketRef.current.emit(REMOTE_ERROR_EVENT, {
              peerConnectionId: activeConnectionIdRef.current,
              targetPeerRoom,
              originPeerRoom,
              originPeerDeviceRole,
              type: "NETWORK_ERROR",
            });
          }
          clearPeerInstance({
            destroyLocalStream: false,
            nextStatus: "error",
            nextError: FRIENDLY_ERROR_MESSAGE,
          });
          scheduleAutoReconnectRef.current?.();
          return;
        }

        const resolvedMessage = isNetworkError ? "NETWORK_ERROR" : error.message;

        if (isNetworkError && socketRef.current && activeConnectionIdRef.current) {
          const targetPeerRoom =
            counterpartPeerRoomRef.current ?? activeConnectionIdRef.current ?? undefined;
          const originPeerRoom = activePeerRoomRef.current ?? undefined;
          const originPeerDeviceRole = activePeerDeviceRoleRef.current ?? undefined;
          socketRef.current.emit(REMOTE_ERROR_EVENT, {
            peerConnectionId: activeConnectionIdRef.current,
            targetPeerRoom,
            originPeerRoom,
            originPeerDeviceRole,
            type: "NETWORK_ERROR",
          });
        }

        clearPeerInstance({
          destroyLocalStream: false,
          nextStatus: "error",
          nextError: resolvedMessage,
        });
        scheduleAutoReconnectRef.current?.();
      });

      peer.on("data", (raw) => {
        try {
          let textPayload: string | null = null;
          const decoder = typeof TextDecoder !== "undefined" ? new TextDecoder() : null;

          if (typeof raw === "string") {
            textPayload = raw;
          } else if (raw instanceof ArrayBuffer) {
            textPayload = decoder?.decode(new Uint8Array(raw)) ?? null;
          } else if (ArrayBuffer.isView(raw)) {
            const view = raw as ArrayBufferView;
            const uintArray =
              view instanceof Uint8Array
                ? view
                : decoder
                ? new Uint8Array(view.buffer, view.byteOffset, view.byteLength)
                : null;
            textPayload = uintArray && decoder ? decoder.decode(uintArray) : null;
          }

          if (!textPayload) {
            console.warn("[PeerConnectionManager] Unable to decode peer data payload");
            return;
          }

          const parsed = JSON.parse(textPayload) as PeerMessage;

          if (parsed?.type === "detection-status" && parsed.payload) {
            const { handsDetected, laptopDetected, handsCount, timestamp } = parsed.payload;
            setRemoteDetectionStatus({
              handsDetected,
              laptopDetected,
              handsCount,
              timestamp,
              receivedAt: Date.now(),
            });
          } else if (parsed?.type === "interview-complete") {
            setInterviewCompleteSignal({
              interviewId: parsed.payload?.interviewId ?? null,
              reason: parsed.payload?.reason ?? null,
              sentAt: parsed.payload?.timestamp ?? null,
              receivedAt: Date.now(),
            });
          }
        } catch (error) {
          console.warn("[PeerConnectionManager] Failed to process peer data message", error);
        }
      });
    },
    [clearPeerInstance, markPeerNegotiationAttempt],
  );

  const createPeer = useCallback(
    ({ initiator, stream }: StartPeerConnectionOptions): SimplePeerInstance | null => {
      try {
        const peer = new SimplePeer({
          initiator,
          trickle: false,
          stream: stream ?? undefined,
          config: { iceServers: LAN_ONLY_ICE_SERVERS },
        });
        setPeerError(null);
        const initialStatus = initiator ? "creating-offer" : "waiting-for-offer";
        setPeerStatus(initialStatus);
        peerStatusRef.current = initialStatus;
        attachPeerListeners(peer);
        peerRef.current = peer;
        return peer;
      } catch (error) {
        console.error("[PeerConnectionManager] Failed to create peer", error);
        setPeerError(error instanceof Error ? error.message : "Failed to create peer connection");
        setPeerStatus("error");
        peerStatusRef.current = "error";
        return null;
      }
    },
    [attachPeerListeners],
  );

  const handleIncomingSignal = useCallback(
    async (payload: { signal?: SignalData; senderSocketId?: string }) => {
      if (!payload?.signal) {
        return;
      }

      const socket = socketRef.current;
      if (!socket) {
        console.warn("[PeerConnectionManager] Received signal without active socket");
        return;
      }

      if (payload.senderSocketId && payload.senderSocketId === socket.id) {
        return;
      }

      markPeerNegotiationAttempt();

      if (!peerRef.current) {
        const created = createPeer({ initiator: false, stream: undefined });
        if (!created) {
          return;
        }
      }

      if (!peerRef.current) {
        return;
      }

      try {
        const maybePeer = peerRef.current as SimplePeerInstance & { _pc?: RTCPeerConnection };
        const currentSignalingState = maybePeer._pc?.signalingState;
        const signalType =
          typeof payload.signal === "object" && payload.signal && "type" in payload.signal
            ? (payload.signal as { type?: string }).type
            : undefined;

        if (
          currentSignalingState === "stable" &&
          signalType === "answer" &&
          (peerStatusRef.current === "connected" || remoteStreamRef.current)
        ) {
          console.warn("[PeerConnectionManager] Ignoring duplicate answer in stable state");
          return;
        }

        setPeerStatus("connecting");
        peerStatusRef.current = "connecting";
        peerRef.current.signal(payload.signal);
      } catch (error) {
        console.error("[PeerConnectionManager] Failed to apply remote signal", error);
        if (socketRef.current && activeConnectionIdRef.current) {
          const targetPeerRoom =
            counterpartPeerRoomRef.current ?? activeConnectionIdRef.current ?? undefined;
          const originPeerRoom = activePeerRoomRef.current ?? undefined;
          const originPeerDeviceRole = activePeerDeviceRoleRef.current ?? undefined;
          socketRef.current.emit(REMOTE_ERROR_EVENT, {
            peerConnectionId: activeConnectionIdRef.current,
            targetPeerRoom,
            originPeerRoom,
            originPeerDeviceRole,
            type: "NETWORK_ERROR",
          });
        }
        clearPeerInstance({
          destroyLocalStream: false,
          nextStatus: "error",
          nextError: FRIENDLY_ERROR_MESSAGE,
        });
        scheduleAutoReconnectRef.current?.();
      }
    },
    [clearPeerInstance, createPeer, markPeerNegotiationAttempt],
  );

  const joinRoom = useCallback(
    (peerConnectionId: string | null | undefined, role: PeerDeviceRole) => {
      if (!peerConnectionId) {
        console.warn("[PeerConnectionManager] joinRoom called without peerConnectionId");
        return;
      }

      const resolvedRole = role;
      const resolvedPeerRoom = derivePeerRoom(peerConnectionId, resolvedRole);
      const counterpartRole = resolveCounterpartRole(resolvedRole);
      const counterpartRoom = derivePeerRoom(peerConnectionId, counterpartRole);

      const shouldReuseConnection =
        activeConnectionIdRef.current === peerConnectionId &&
        activePeerDeviceRoleRef.current === resolvedRole &&
        socketRef.current;

      if (shouldReuseConnection && socketRef.current && socketRef.current.connected) {
        return;
      }

      clearPeerInstance({
        destroyLocalStream: false,
        nextStatus: "idle",
      });

      if (!socketRef.current) {
        const socketUrl = resolveSocketUrl();
        if (!socketUrl) {
          console.warn("[PeerConnectionManager] Unable to resolve socket URL");
          return;
        }
        const connectionMeta = {
          peerConnectionId,
          peerDeviceRole: resolvedRole,
          peerConnectionRoom: resolvedPeerRoom,
        };
        socketRef.current = io(socketUrl, {
          transports: TRANSPORTS,
          autoConnect: false,
          auth: connectionMeta,
          query: connectionMeta,
        });
      }

      const socket = socketRef.current;
      socket.removeAllListeners();

      socket.on("connect", () => {
        setIsSocketConnected(true);
        setSocketId(socket.id ?? null);
      });

      socket.on("disconnect", () => {
        setIsSocketConnected(false);
        setSocketId(null);
      });

      socket.on("peer-connection:signal", handleIncomingSignal);

      socket.on("peer-connection:mobile-joined", () => {
        setMobileHasJoined(true);
      });

      socket.on(REMOTE_ERROR_EVENT, () => {
        clearPeerInstance({
          destroyLocalStream: false,
          nextStatus: "error",
          nextError: FRIENDLY_ERROR_MESSAGE,
        });
        scheduleAutoReconnectRef.current?.();
      });

      socket.on("peer-connection:lan-match", (payload) => {
        setLanStatus("matched");
        setLanStatusDetail(payload ?? null);
      });

      socket.on("peer-connection:lan-mismatch", (payload) => {
        setLanStatus("mismatched");
        setLanStatusDetail(payload ?? null);
      });

      socket.on(REMOTE_TERMINATED_EVENT, () => {
        clearPeerInstance({
          destroyLocalStream: true,
          nextStatus: "closed",
          nextError: FRIENDLY_ERROR_MESSAGE,
        });
      });

      socketRef.current = socket;
      activeConnectionIdRef.current = peerConnectionId;
      activePeerRoomRef.current = resolvedPeerRoom;
      counterpartPeerRoomRef.current = counterpartRoom;
      activePeerDeviceRoleRef.current = resolvedRole;
      enableAutoReconnect();

      if (socket.disconnected) {
        socket.connect();
      } else {
        setIsSocketConnected(true);
        setSocketId(socket.id ?? null);
      }

      lastRequestedConnectionIdRef.current = peerConnectionId;
      lastRequestedDeviceRoleRef.current = resolvedRole;
    },
    [clearPeerInstance, enableAutoReconnect, handleIncomingSignal],
  );

  const ensureSocketConnection = useCallback(async (timeoutMs = 3000) => {
    const socket = socketRef.current;
    if (!socket) {
      throw new Error("Socket connection is not initialised yet.");
    }
    if (socket.connected) {
      return;
    }

    if (socketConnectionPromiseRef.current) {
      return socketConnectionPromiseRef.current;
    }

    socketConnectionPromiseRef.current = new Promise<void>((resolve, reject) => {
      const cleanup = () => {
        clearTimeout(timeoutId);
        socket.off("connect", handleConnect);
        socket.off("connect_error", handleError);
        socketConnectionPromiseRef.current = null;
      };

      const timeoutId = setTimeout(() => {
        cleanup();
        reject(new Error("Timed out while establishing secure connection to interviewer."));
      }, timeoutMs);

      const handleConnect = () => {
        cleanup();
        resolve();
      };

      const handleError = (error: unknown) => {
        cleanup();
        reject(error instanceof Error ? error : new Error(String(error)));
      };

      socket.once("connect", handleConnect);
      socket.once("connect_error", handleError);

      if (socket.disconnected) {
        socket.connect();
      }
    });

    return socketConnectionPromiseRef.current;
  }, []);

  const startPeerConnection = useCallback(
    async ({ initiator, stream }: StartPeerConnectionOptions) => {
      if (!socketRef.current || !socketRef.current.connected) {
        console.warn("[PeerConnectionManager] Cannot start peer connection without an active socket");
        return;
      }

      setPeerError(null);
      clearPeerInstance({
        destroyLocalStream: false,
        nextStatus: "preparing",
        nextError: null,
      });

      if (stream) {
        localStreamRef.current = stream;
        setLocalStream(stream);
      } else if (!initiator) {
        localStreamRef.current = null;
        setLocalStream(null);
      }

      const effectiveStream = stream ?? localStreamRef.current ?? undefined;
      void createPeer({ initiator, stream: effectiveStream });
      lastStartPeerOptionsRef.current = { initiator };
    },
    [clearPeerInstance, createPeer],
  );

  const scheduleAutoReconnect = useCallback(
    (options: AutoReconnectOptions = {}) => {
      if (!autoReconnectEnabledRef.current) {
        return;
      }
      if (!lastStartPeerOptionsRef.current) {
        return;
      }
      if (!socketRef.current) {
        return;
      }
      if (autoReconnectAttemptsRef.current >= AUTO_RECONNECT_MAX_ATTEMPTS) {
        return;
      }

      const delayMs = options.delayMs ?? AUTO_RECONNECT_DELAY_MS;
      const destroyLocalStream = options.destroyLocalStream ?? false;
      autoReconnectAttemptsRef.current += 1;
      cancelScheduledAutoReconnect();

      autoReconnectTimeoutRef.current = setTimeout(() => {
        autoReconnectTimeoutRef.current = null;
        void (async () => {
          if (destroyLocalStream) {
            stopLocalTracks();
          }
          try {
            await ensureSocketConnection().catch(() => undefined);
          } catch {
            // noop
          }
          try {
            await startPeerConnection({
              initiator: lastStartPeerOptionsRef.current?.initiator ?? false,
            });
          } catch (error) {
            console.warn("[PeerConnectionManager] Auto-reconnect attempt failed", error);
          }
        })();
      }, delayMs);
    },
    [cancelScheduledAutoReconnect, ensureSocketConnection, startPeerConnection, stopLocalTracks],
  );

  const triggerPeerReconnect = useCallback(() => {
    if (!lastStartPeerOptionsRef.current) {
      console.warn("[PeerConnectionManager] Cannot trigger reconnect: no prior start options recorded");
      return;
    }
    scheduleAutoReconnect({ destroyLocalStream: false, delayMs: 100 });
    clearPeerInstance({
      destroyLocalStream: false,
      nextStatus: "closed",
      nextError: FRIENDLY_ERROR_MESSAGE,
    });
  }, [clearPeerInstance, scheduleAutoReconnect]);

  const sendPeerMessage = useCallback((message: PeerMessage) => {
    const peer = peerRef.current;
    if (!peer) {
      console.warn("[PeerConnectionManager] Cannot send data message: peer is not initialised");
      return false;
    }
    if (!peer.connected) {
      console.warn("[PeerConnectionManager] Cannot send data message: peer is not connected");
      return false;
    }
    try {
      peer.send(JSON.stringify(message));
      return true;
    } catch (error) {
      console.error("[PeerConnectionManager] Failed to send peer data message", error);
      return false;
    }
  }, []);

  const exitRoom = useCallback(
    (destroyLocalStream = false) => {
      disableAutoReconnect();
      lastStartPeerOptionsRef.current = null;
      if (socketRef.current && activeConnectionIdRef.current) {
        const targetPeerRoom =
          counterpartPeerRoomRef.current ?? activeConnectionIdRef.current ?? undefined;
        const originPeerRoom = activePeerRoomRef.current ?? undefined;
        const originPeerDeviceRole = activePeerDeviceRoleRef.current ?? undefined;
        socketRef.current.emit("peer-connection:terminate", {
          peerConnectionId: activeConnectionIdRef.current,
          targetPeerRoom,
          originPeerRoom,
          originPeerDeviceRole,
        });
      }

      clearPeerInstance({
        destroyLocalStream,
        nextStatus: destroyLocalStream ? "closed" : "idle",
        nextError: destroyLocalStream ? FRIENDLY_ERROR_MESSAGE : null,
      });
      clearSocketInstance();
      lastRequestedConnectionIdRef.current = null;
      lastRequestedDeviceRoleRef.current = null;
    },
    [clearPeerInstance, clearSocketInstance, disableAutoReconnect],
  );

  const reconnectToLastRoom = useCallback(() => {
    if (!lastRequestedConnectionIdRef.current || !lastRequestedDeviceRoleRef.current) {
      console.warn("[PeerConnectionManager] Cannot reconnect without a previous room id");
      return;
    }
    joinRoom(lastRequestedConnectionIdRef.current, lastRequestedDeviceRoleRef.current);
  }, [joinRoom]);

  const reconnect = useCallback(
    (destroyLocalStream = true) => {
      clearPeerInstance({
        destroyLocalStream,
        nextStatus: "idle",
        nextError: null,
      });
      reconnectToLastRoom();
    },
    [clearPeerInstance, reconnectToLastRoom],
  );

  const emitMobileJoined = useCallback((peerConnectionId: string) => {
    if (!socketRef.current || !socketRef.current.connected) {
      console.warn("[PeerConnectionManager] Cannot emit mobile-joined: socket not connected");
      return;
    }
    const activeRole = activePeerDeviceRoleRef.current ?? "mobile";
    const targetPeerRoom =
      counterpartPeerRoomRef.current ??
      derivePeerRoom(peerConnectionId, resolveCounterpartRole(activeRole));
    const originPeerRoom = activePeerRoomRef.current ?? undefined;
    const originPeerDeviceRole = activePeerDeviceRoleRef.current ?? undefined;

    socketRef.current.emit("peer-connection:mobile-joined", {
      peerConnectionId,
      targetPeerRoom,
      originPeerRoom,
      originPeerDeviceRole,
    });
  }, []);

  useEffect(() => {
    const handleOffline = () => {
      clearPeerInstance({
        destroyLocalStream: true,
        nextStatus: "closed",
        nextError: NETWORK_DROP_MESSAGE,
      });
      clearSocketInstance();
    };
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("offline", handleOffline);
    };
  }, [clearPeerInstance, clearSocketInstance]);

  useEffect(() => {
    if (peerStatus === "connected") {
      setPeerError(null);
    }
    peerStatusRef.current = peerStatus;
  }, [peerStatus]);

  useEffect(() => {
    if (peerStatus === "connected") {
      autoReconnectAttemptsRef.current = 0;
      cancelScheduledAutoReconnect();
    }
  }, [peerStatus, cancelScheduledAutoReconnect]);

  useEffect(() => {
    return () => {
      resetConnectionState(true);
    };
  }, [resetConnectionState]);

  useEffect(() => {
    remoteStreamRef.current = remoteStream;
  }, [remoteStream]);

  useEffect(() => {
    scheduleAutoReconnectRef.current = scheduleAutoReconnect;
    return () => {
      if (scheduleAutoReconnectRef.current === scheduleAutoReconnect) {
        scheduleAutoReconnectRef.current = null;
      }
    };
  }, [scheduleAutoReconnect]);

  useEffect(() => {
    return () => {
      cancelScheduledAutoReconnect();
    };
  }, [cancelScheduledAutoReconnect]);

  useEffect(() => {
    if (lanStatus !== "matched") {
      setLanMatchedPeerFailed(false);
      return;
    }
    if (isPeerConnected) {
      setLanMatchedPeerFailed(false);
      return;
    }
    if (hasAttemptedPeerConnection && (peerStatus === "error" || peerStatus === "closed")) {
      setLanMatchedPeerFailed(true);
    }
  }, [lanStatus, peerStatus, isPeerConnected, hasAttemptedPeerConnection]);

  return {
    joinRoom,
    startPeerConnection,
    ensureSocketConnection,
    exitRoom,
    reconnectToLastRoom,
    reconnect,
    triggerPeerReconnect,
    emitMobileJoined,
    isSocketConnected,
    socketId,
    peerStatus,
    peerError,
    isPeerConnected,
    remoteStream,
    remoteStreamVersion,
    localStream,
    mobileHasJoined,
    sendPeerMessage,
    remoteDetectionStatus,
    interviewCompleteSignal,
    lanStatus,
    lanStatusDetail,
    lanMatchedPeerFailed,
    isMobileBypassed,
    markMobileBypassed,
  };
};

export default usePeerConnectionManager;
export type PeerConnectionManager = ReturnType<typeof usePeerConnectionManager>;
